/**
 * DrawerHeader — the S30-style page header for `/petty-cash`.
 *
 * Mirrors `prototype/cash/petty-cash.html ▸ .acct-head`: wallet avatar + title,
 * a meta line (imprest · held-by · org), a pill row signalling Cash type +
 * imprest utilisation + last count + Primary, and three action buttons
 * (New voucher · Replenish · Count) wired to the in-feature routes.
 *
 * Pure presentation — the route owns the data fetch and supplies the
 * already-shaped values. Action buttons are TanStack Router `<Link>`s so
 * the V1 SPA behaviour stays SPA-internal (no full reload).
 *
 * @see prototype/cash/petty-cash.html `.acct-head`
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §10
 */
import { Link } from "@tanstack/react-router";
import { Wallet, Plus, ArrowDownToLine, Check } from "lucide-react";
import { Button, cn } from "@flint/ui";
import { paiseToInr } from "../../../lib/format.ts";

// Phase 1b: routes for the voucher / replenish / count flows land in 1c
// dispatch. The `as string` widens the literal so the Link's typed
// `to` prop accepts it; once the routes register the cast becomes a no-op.
const ROUTE_NEW = "/cash/petty-cash/new" as string;
const ROUTE_REPLENISH = "/cash/petty-cash/replenish" as string;
const ROUTE_COUNT = "/cash/petty-cash/count" as string;

export interface DrawerHeaderProps {
  /** Drawer display name — `bank_accounts.name`. */
  name: string;
  /** Imprest ceiling in paise (stringified bigint). */
  imprestPaise: string;
  /** Current running balance in paise. */
  balancePaise: string;
  /** Days since the last `cash_count_adjustment` for this drawer. `null` = never counted. */
  daysSinceLastCount: number | null;
  /** Custodian display name. `null` until membership tagging lands (V1.x). */
  custodian: string | null;
  /** Operator display name. `null` until membership tagging lands (V1.x). */
  operator: string | null;
}

export function DrawerHeader({
  name,
  imprestPaise,
  balancePaise,
  daysSinceLastCount,
  custodian,
  operator,
}: DrawerHeaderProps) {
  const imprestUtilPct = computeImprestUtilPct(imprestPaise, balancePaise);
  const heldBy = operator ?? custodian ?? null;

  return (
    <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3 mb-1.5">
          <span
            aria-hidden
            className="inline-grid place-items-center w-10 h-10 rounded-sm bg-sky-50 text-sky-700 border border-sky-100 shrink-0"
          >
            <Wallet size={20} strokeWidth={2} />
          </span>
          <h1 className="text-2xl font-bold tracking-tight text-ink truncate">{name}</h1>
        </div>

        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted mb-2.5">
          <span>
            Imprest{" "}
            <strong className="num text-ink-2 font-medium">{paiseToInr(imprestPaise)}</strong>
          </span>
          {heldBy ? (
            <>
              <Dot />
              <span>
                Held by <strong className="text-ink-2 font-medium">{heldBy}</strong>
              </span>
            </>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <Pill tone="cash">
            <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-current shrink-0" />
            Cash
          </Pill>
          {imprestUtilPct !== null ? <Pill tone="warn">Imprest {imprestUtilPct}%</Pill> : null}
          {daysSinceLastCount !== null ? (
            <Pill tone="ok">
              <Check size={10} strokeWidth={3} aria-hidden />
              Counted {daysSinceLastCount === 0 ? "today" : `${daysSinceLastCount} days ago`}
            </Pill>
          ) : (
            <Pill tone="warn">Never counted</Pill>
          )}
          <Pill tone="primary">Primary</Pill>
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-2 shrink-0">
        <Button asChild intent="primary" size="md">
          <Link to={ROUTE_NEW}>
            <Plus size={13} strokeWidth={2.4} aria-hidden />
            New voucher
          </Link>
        </Button>
        <Button asChild intent="ghost" size="md" className="border border-line">
          <Link to={ROUTE_REPLENISH}>
            <ArrowDownToLine size={13} strokeWidth={2} aria-hidden />
            Replenish
          </Link>
        </Button>
        <Button asChild intent="ghost" size="md" className="border border-line">
          <Link to={ROUTE_COUNT}>
            <Check size={13} strokeWidth={2} aria-hidden />
            Count
          </Link>
        </Button>
      </div>
    </header>
  );
}

// ── internals ──────────────────────────────────────────────────────

function Dot() {
  return <span aria-hidden className="w-[3px] h-[3px] rounded-full bg-line-2 inline-block" />;
}

type PillTone = "cash" | "warn" | "ok" | "primary";

const PILL_TONE: Record<PillTone, string> = {
  cash: "bg-raised text-ink-2 border-line",
  warn: "bg-warn-50 text-warn border-warn-100",
  ok: "bg-ok-50 text-ok border-ok-100",
  primary: "bg-sky-50 text-sky-700 border-sky-100",
};

function Pill({ tone, children }: { tone: PillTone; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 h-[22px] px-2 text-xs font-semibold rounded-full border whitespace-nowrap",
        PILL_TONE[tone],
      )}
    >
      {children}
    </span>
  );
}

/**
 * Integer-paise math: returns `100 * (imprest − balance) / imprest` rounded
 * to whole percent. Returns `null` when imprest is 0 (not an imprest drawer).
 */
function computeImprestUtilPct(imprestPaise: string, balancePaise: string): number | null {
  const imprest = BigInt(imprestPaise || "0");
  if (imprest === 0n) return null;
  const balance = BigInt(balancePaise || "0");
  const spent = imprest - balance;
  if (spent <= 0n) return 0;
  return Number((spent * 100n) / imprest);
}
